/**
 * Fetches a feed row for a `template_components` tab — same RPCs the
 * StayUpToDate loaders use before {@link handleFirstImage} stores `card_image`.
 * Prefers the first row that has a usable image (skips text-only leading posts).
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
// Tab cover / first-item RPCs on public + editor preview — session-free reads.
import { supabasePublic as supabase } from "../../../config/supabaseClient";
import { computeWindow } from "../../../Utils/customHooks/UpToDateContent/useLoadYesterdayLinks";
import {
  getAllDatesFromSelection,
  getFirstUsableImageUrlForCard,
} from "../../../Utils/StayUpToDate/RenderComponent";
import { StaticVariables } from "../../../Utils/StaticVariables";
import { buildPhraseIlikePatterns } from "../../../components/SearchInput/phraseSearchHelpers";
import {
  deriveAllowedPlatforms,
  readPlatformFilterFromDetails,
  shouldUseShopKeywordRpc,
  type ShowFilter,
} from "../LandingPageCarousels/QueryBasedCarousel/filterUtils";
import {
  CONTENT_FEED_ALL_FEED_TYPES,
  CONTENT_FEED_ALL_SOCIALS,
} from "../StayUpToDate/AddComponentPicker/contentFeedSelection";
import { getContentFeedDateWindowFromComponent } from "../StayUpToDate/AddComponentPicker/contentFeedTimeframe";
import { predictStayUpToDateTabHasData } from "./templateTabEligibility";

dayjs.extend(utc);
dayjs.extend(timezone);

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Scan past text-only leading posts when resolving a card cover. */
const CARD_IMAGE_SCAN_LIMIT = 5;

function rpcRows(result: unknown): Record<string, unknown>[] {
  if (!result || typeof result !== "object") return [];
  const root = result as Record<string, unknown>;
  const payload = root.data ?? result;
  if (Array.isArray(payload)) {
    return payload.filter(
      (row): row is Record<string, unknown> =>
        Boolean(row) && typeof row === "object",
    );
  }
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const nested = (payload as Record<string, unknown>).data;
    if (Array.isArray(nested)) {
      return nested.filter(
        (row): row is Record<string, unknown> =>
          Boolean(row) && typeof row === "object",
      );
    }
  }
  return [];
}

function firstRpcRowWithImage(
  result: unknown,
): Record<string, unknown> | null {
  const rows = rpcRows(result);
  for (const row of rows) {
    if (getFirstUsableImageUrlForCard(row)) return row;
  }
  return rows[0] ?? null;
}

function buildSelectedDatesArray(
  displayNameRaw: string | undefined,
): string[] | null {
  if (!displayNameRaw?.trim()) return null;
  const selectedDays = displayNameRaw.split(",").map((s) => s.trim());
  if (selectedDays.length === 0) return null;
  const dates = getAllDatesFromSelection(selectedDays);
  if (dates.length === 0) return null;
  return dates.map((dateStr) => {
    const date = new Date(dateStr);
    return dayjs(date).tz(userTimezone).format("YYYY-MM-DD");
  });
}

async function fetchShoplinkDayFirst(
  row: Record<string, unknown>,
  userId: string,
  lcUser: string,
): Promise<Record<string, unknown> | null> {
  const displayName = String(row.display_name ?? "").trim();
  const mappings = StaticVariables().dayMappings as Record<string, unknown>;

  let numberOfDays: number[] | null = null;
  let componentType = "";
  if (displayName === "Yesterday") numberOfDays = [1];
  else if (displayName === "This Week") numberOfDays = [7];
  else if (displayName === "This Month") numberOfDays = [30];
  else if (mappings[displayName]) {
    numberOfDays = [];
    componentType = "daywise";
  } else {
    return null;
  }

  const selectedDaySegments = row.display_name
    ? String(row.display_name).split(",").map((s) => s.trim())
    : undefined;

  const windowRange = computeWindow(
    numberOfDays,
    componentType,
    selectedDaySegments,
  );
  if (!windowRange.start || !windowRange.end) return null;

  const startDate = windowRange.start.split(" ")[0];
  const endDate = windowRange.end.split(" ")[0];

  const filterRaw = (row as { shoppable_content_filter?: string })
    .shoppable_content_filter;
  const activeFilter = (filterRaw || "both").toLowerCase();
  const sv = StaticVariables();
  let socials = sv.PLATFORMS as string[];
  if (activeFilter === "content") socials = sv.CONTENT_PLATFORMS;
  else if (activeFilter === "products") socials = sv.PRODUCT_PLATFORMS;

  const customOrderRaw = (row as { custom_order?: string }).custom_order;
  const customOrder =
    typeof customOrderRaw === "string" && customOrderRaw.trim()
      ? customOrderRaw.split(",").map((s: string) => s.trim()).filter(Boolean)
      : [];

  const removedPostIds = (row as { removed_postids?: unknown }).removed_postids;
  const processedRemovedPostIds = Array.isArray(removedPostIds)
    ? removedPostIds.map(String).filter(Boolean)
    : typeof removedPostIds === "string"
      ? removedPostIds.split(",").map((s) => s.trim()).filter(Boolean)
      : [];

  const order = (row as { content_order?: string | boolean }).content_order;
  const normalizedOrder =
    order === false || order === "false" ? "ASC" : order;

  const { data: searchResult, error } = await supabase.rpc(
    "get_shoplink_content",
    {
      userid: userId,
      user_name: lcUser,
      start_date: startDate,
      end_date: endDate,
      post_ids: [],
      order_type: String(normalizedOrder ?? "DESC"),
      custom_order: customOrder,
      socials,
      limit_num: CARD_IMAGE_SCAN_LIMIT,
      offset_num: 0,
      removed_post_ids: processedRemovedPostIds,
      component_type: componentType || null,
      selected_dates: buildSelectedDatesArray(String(row.display_name ?? "")),
      user_timezone: userTimezone,
    },
  );

  if (error) return null;
  return firstRpcRowWithImage(searchResult);
}

async function fetchThisWeekContentFirst(
  row: Record<string, unknown>,
  userId: string,
  lcUser: string,
): Promise<Record<string, unknown> | null> {
  const sv = StaticVariables();
  const contentPlatforms = sv.CONTENT_PLATFORMS;
  const contentOrder = String(
    (row as { content_order?: string }).content_order || "DESC",
  );
  const customOrderPostIds =
    typeof (row as { custom_order?: string }).custom_order === "string"
      ? (row as { custom_order: string }).custom_order
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

  const numDayStart = dayjs()
    .utc()
    .subtract(7, "day")
    .startOf("day")
    .format("YYYY-MM-DD HH:mm:ss");
  const numDayEnd = dayjs().utc().format("YYYY-MM-DD HH:mm:ss");

  const { data: result, error } = await supabase.rpc("get_yesterday_content", {
    userid: userId,
    user_name: lcUser,
    start_date: numDayStart,
    end_date: numDayEnd,
    post_ids: [],
    order_type: contentOrder,
    content_platforms: contentPlatforms,
    custom_order: customOrderPostIds,
    limit_num: CARD_IMAGE_SCAN_LIMIT,
    offset_num: 0,
    removed_post_ids: [],
  });

  if (error) return null;
  return firstRpcRowWithImage(result);
}

async function fetchCustomOrderedFirst(
  row: Record<string, unknown>,
  userId: string,
  lcUser: string,
  feedType: string,
): Promise<Record<string, unknown> | null> {
  const raw = (row as { post_ids?: unknown }).post_ids;
  const postIdsArr = Array.isArray(raw)
    ? raw.map(String).filter(Boolean)
    : typeof raw === "string"
      ? raw.split(",").map((s) => s.trim()).filter(Boolean)
      : [];
  if (postIdsArr.length === 0) return null;

  const staticVars = StaticVariables();
  const filterRaw = (row as { shoppable_content_filter?: string })
    .shoppable_content_filter;
  const activeFilter = (
    filterRaw || (feedType === "content" ? "content" : "products")
  ).toLowerCase();

  let platforms = staticVars.PLATFORMS as string[];
  if (activeFilter === "content") platforms = staticVars.CONTENT_PLATFORMS;
  else if (activeFilter === "products") platforms = staticVars.PRODUCT_PLATFORMS;

  const customOrderRaw = (row as { custom_order?: string }).custom_order;
  const customOrder =
    typeof customOrderRaw === "string" && customOrderRaw.trim()
      ? customOrderRaw.split(",").map((s: string) => s.trim()).filter(Boolean)
      : null;

  const { data: rpcResult, error } = await supabase.rpc(
    "get_custom_content_orderedd",
    {
      userid: userId,
      user_name: lcUser,
      post_ids: postIdsArr,
      order_type: String(
        (row as { content_order?: string }).content_order || "DESC",
      ),
      custom_order: customOrder,
      platforms,
      limit_num: CARD_IMAGE_SCAN_LIMIT,
      removed_post_ids: [],
    },
  );

  if (error) return null;
  return firstRpcRowWithImage(rpcResult);
}

async function fetchTypeContentFirst(
  row: Record<string, unknown>,
  userId: string,
  lcUser: string,
  platform: string,
  feedType: string,
): Promise<Record<string, unknown> | null> {
  const orderType = String(
    (row as { content_order?: string }).content_order || "DESC",
  );
  const customOrder =
    typeof (row as { custom_order?: string }).custom_order === "string"
      ? (row as { custom_order: string }).custom_order
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  const pinned =
    typeof (row as { pinned_posts?: string }).pinned_posts === "string"
      ? (row as { pinned_posts: string }).pinned_posts
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  const statusFilter = String(
    (row as { shoppable_status_filter?: string }).shoppable_status_filter ||
      "all",
  );

  if (feedType.toUpperCase() === "STORY") {
    const today = dayjs().utc().format("YYYY-MM-DD");
    const yesterday = dayjs().utc().subtract(7, "day").format("YYYY-MM-DD");
    const { data: content, error } = await supabase.rpc(
      "get_storytypecontenttteonord_pin_new",
      {
        userid: userId,
        user_name: lcUser,
        platform_name: platform,
        feed_type: "STORY",
        start_date: yesterday,
        end_date: today,
        order_type: orderType,
        custom_order: customOrder,
        limit_num: CARD_IMAGE_SCAN_LIMIT,
        offset_num: 0,
        pinned_post_ids: pinned,
        shoppable_status_filter: statusFilter,
      },
    );
    if (error) return null;
    return firstRpcRowWithImage(content);
  }

  const feedTypes =
    platform.toLowerCase() === "linkedin" ? ["POST"] : [feedType.toUpperCase()];
  const { data: content, error } = await supabase.rpc(
    "get_content_type_postsspinn_new",
    {
      userid: userId,
      user_name: lcUser,
      platform_name: platform,
      feed_type: feedTypes,
      order_type: orderType,
      custom_order: customOrder,
      limit_num: CARD_IMAGE_SCAN_LIMIT,
      offset_num: 0,
      pinned_post_ids: pinned,
      shoppable_status_filter: statusFilter,
    },
  );

  if (error) return null;
  return firstRpcRowWithImage(content);
}

/** Same RPC + filters as FeaturedData / useLoadTypeContentData(type: FEATURE). */
async function fetchFeaturedFirst(
  row: Record<string, unknown>,
  userId: string,
  lcUser: string,
): Promise<Record<string, unknown> | null> {
  const orderType = String(
    (row as { content_order?: string }).content_order || "DESC",
  );
  const customOrder =
    typeof (row as { custom_order?: string }).custom_order === "string"
      ? (row as { custom_order: string }).custom_order
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  const pinned =
    typeof (row as { pinned_posts?: string }).pinned_posts === "string"
      ? (row as { pinned_posts: string }).pinned_posts
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  const statusFilter = String(
    (row as { shoppable_status_filter?: string }).shoppable_status_filter ||
      "all",
  );

  const { data: content, error } = await supabase.rpc(
    "get_featuredddataaeeord_pin_new",
    {
      userid: userId,
      filter: [
        "FEED",
        "REELS",
        "STORY",
        "HIGHLIGHTS",
        "PIN",
        "VIDEO",
        "PODCAST",
        "TIKTOK",
        "MAIL",
        "TWEET",
        "PRODUCT",
        "POST",
      ],
      query_str: "",
      user_name: lcUser,
      socials: [
        "instagram",
        "youtube",
        "tiktok",
        "podcast",
        "pinterest",
        "newsletter",
        "twitter",
        "LTK",
        "Amazon",
        "ShopMy",
        "MISC",
        "linkedin",
        "MISC-content",
      ],
      order_type: orderType,
      custom_order: customOrder,
      limit_num: CARD_IMAGE_SCAN_LIMIT,
      offset_num: 0,
      pinned_post_ids: pinned,
      shoppable_status_filter: statusFilter,
    },
  );

  if (error) return null;
  return firstRpcRowWithImage(content);
}

async function fetchQueryBasedFirst(
  row: Record<string, unknown>,
  userId: string,
  lcUser: string,
  isShopLinks: boolean,
): Promise<Record<string, unknown> | null> {
  const queryTerm = String((row as { query?: string }).query ?? "").trim();
  if (!queryTerm) return null;

  const platformFilter = readPlatformFilterFromDetails(
    (row as { details?: unknown }).details,
  );
  const showFilter = (
    ((row as { shoppable_content_filter?: string }).shoppable_content_filter ||
      "both") as string
  ).toLowerCase() as ShowFilter;
  const showSocials = deriveAllowedPlatforms(
    platformFilter,
    showFilter,
    isShopLinks,
  );

  const sv = StaticVariables();
  const normalizedQuery = queryTerm.toLowerCase().replace(/\s+/g, " ").trim();
  let words = normalizedQuery.split(" ");
  let localContentOrder = String(
    (row as { content_order?: string }).content_order ||
      sv.DEFAULT_CONTENT_ORDER ||
      "DESC",
  );
  if (words.length === 1 && !sv.SEARCH_EXCEPTIONS.includes(normalizedQuery)) {
    const suffixRegex = /(s)$/i;
    if (suffixRegex.test(words[0])) {
      words = [words[0].replace(suffixRegex, "")];
    }
  }
  if (words.includes("shop") || words.includes("buy")) {
    localContentOrder = "shoppable";
    words = words.filter((w) => w !== "shop" && w !== "buy");
  }
  const filteredWords = words.filter(
    (word: string) => !sv.connectingWords.includes(word.toLowerCase()),
  );
  const joined = filteredWords.join(" ");
  const pgQuery = joined.toLowerCase().replace(/\s+/g, " & ");
  const singlePatterns = buildPhraseIlikePatterns(normalizedQuery);
  const localOrder = localContentOrder.split(" ")[0].toLowerCase();

  const customOrderPostIds =
    typeof (row as { custom_order?: string }).custom_order === "string"
      ? (row as { custom_order: string }).custom_order
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

  const removedPostIds = (row as { removed_postids?: unknown }).removed_postids;
  const normalizedRemovedPostIds = Array.isArray(removedPostIds)
    ? removedPostIds.map(String).filter(Boolean)
    : typeof removedPostIds === "string"
      ? removedPostIds.split(",").map((id) => id.trim()).filter(Boolean)
      : [];

  const rpcFunction = isShopLinks ? "query_based_shop" : "query_based_cont";
  const { data: searchResult, error } = await supabase.rpc(rpcFunction, {
    query_string: pgQuery,
    userid: userId,
    username: lcUser,
    socials: showSocials,
    content_order: localOrder,
    single: singlePatterns,
    post_order: String(
      (row as { content_order?: string }).content_order || "DESC",
    ),
    custom_order: customOrderPostIds,
    limit_num: CARD_IMAGE_SCAN_LIMIT,
    offset_num: 0,
    removed_post_ids: normalizedRemovedPostIds,
  });

  if (error) return null;
  return firstRpcRowWithImage(searchResult);
}

/** First feed item for a `template_components` row (content tabs). */
async function fetchAllPlatformsContentFirst(
  row: Record<string, unknown>,
  userId: string,
  lcUser: string,
): Promise<Record<string, unknown> | null> {
  const orderType = String(
    (row as { content_order?: string }).content_order || "DESC",
  );
  const customOrder =
    typeof (row as { custom_order?: string }).custom_order === "string"
      ? (row as { custom_order: string }).custom_order
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  const pinned =
    typeof (row as { pinned_posts?: string }).pinned_posts === "string"
      ? (row as { pinned_posts: string }).pinned_posts
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  const statusFilter = String(
    (row as { shoppable_status_filter?: string }).shoppable_status_filter ||
      "all",
  );
  const dateWindow = getContentFeedDateWindowFromComponent(row);
  const allBase = {
    userid: userId,
    filter: [...CONTENT_FEED_ALL_FEED_TYPES],
    query_str: "",
    user_name: lcUser,
    socials: [...CONTENT_FEED_ALL_SOCIALS],
    order_type: orderType,
    custom_order: customOrder,
    limit_num: CARD_IMAGE_SCAN_LIMIT,
    offset_num: 0,
    pinned_post_ids: pinned,
    shoppable_status_filter: statusFilter,
  };
  const { data: content, error } = dateWindow.applyFilter
    ? await supabase.rpc("get_content_all_platforms_dated", {
        ...allBase,
        start_date: dateWindow.startDate,
        end_date: dateWindow.endDate,
        component_type: dateWindow.componentType,
        selected_dates: dateWindow.selectedDates,
        user_timezone: userTimezone,
      })
    : await supabase.rpc("get_content_all_platforms_new", allBase);
  if (error) return null;
  return firstRpcRowWithImage(content);
}

export async function fetchFirstTemplateTabFeedItem(
  userId: string,
  username: string,
  row: Record<string, unknown> | undefined | null,
): Promise<Record<string, unknown> | null> {
  if (!row || !predictStayUpToDateTabHasData(row)) return null;

  const type = String(row.type ?? "").toLowerCase().trim();
  const lcUser = username.trim().toLowerCase();
  const displayName = String(
    (row as { display_name?: string }).display_name ?? "",
  ).trim();

  if (displayName === "This Week Content") {
    return fetchThisWeekContentFirst(row, userId, lcUser);
  }

  const typeLc = type.toLowerCase();
  const dnLc = displayName.toLowerCase();
  const match = (v: string) => v === typeLc || v === dnLc;

  // Recent Content / Content Feed — platform "All"
  if (
    type === "all" ||
    match("all") ||
    dnLc === "recent content" ||
    dnLc.includes("content feed")
  ) {
    return fetchAllPlatformsContentFirst(row, userId, lcUser);
  }

  if (match("tiktok")) {
    return fetchTypeContentFirst(row, userId, lcUser, "tiktok", "TIKTOK");
  }
  if (match("twitter")) {
    return fetchTypeContentFirst(row, userId, lcUser, "twitter", "TWEET");
  }
  if (match("youtube")) {
    return fetchTypeContentFirst(row, userId, lcUser, "youtube", "VIDEO");
  }
  if (match("podcast")) {
    return fetchTypeContentFirst(row, userId, lcUser, "podcast", "PODCAST");
  }
  if (match("linkedin")) {
    // Stay loaders use feed_type POST for LinkedIn (not LINKEDIN).
    return fetchTypeContentFirst(row, userId, lcUser, "linkedin", "POST");
  }
  if (match("instagram story") || match("story")) {
    return fetchTypeContentFirst(row, userId, lcUser, "instagram", "STORY");
  }

  const mappings = StaticVariables().dayMappings as Record<string, unknown>;
  const isDayBasedShoplink =
    displayName === "Yesterday" ||
    displayName === "This Week" ||
    displayName === "This Month" ||
    Boolean(mappings[displayName]) ||
    type === "daywise";

  if (isDayBasedShoplink) {
    return fetchShoplinkDayFirst(row, userId, lcUser);
  }

  if (type === "links" || type === "content" || type === "shoppable") {
    return fetchCustomOrderedFirst(
      row,
      userId,
      lcUser,
      type === "shoppable" ? "links" : type,
    );
  }

  if (type === "query-content" || type === "content-series") {
    return fetchQueryBasedFirst(row, userId, lcUser, false);
  }

  if (type === "query-links") {
    return fetchQueryBasedFirst(
      row,
      userId,
      lcUser,
      shouldUseShopKeywordRpc(
        type,
        (row as { shoppable_content_filter?: string }).shoppable_content_filter,
      ),
    );
  }

  if (type === "reels" || type === "featured" || type === "videos") {
    const rawPid = (row as { post_ids?: unknown }).post_ids;
    const postIdsArr = Array.isArray(rawPid)
      ? rawPid.map(String).filter(Boolean)
      : typeof rawPid === "string"
        ? rawPid.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
    if (postIdsArr.length > 0) {
      return fetchCustomOrderedFirst(row, userId, lcUser, type);
    }
    if (type === "reels") {
      const filterLc = String(
        (row as { shoppable_content_filter?: string }).shoppable_content_filter ||
          "",
      )
        .trim()
        .toLowerCase();
      const detailsRaw = String((row as { details?: string }).details || "").trim();
      let feedKindProduct = false;
      if (detailsRaw.startsWith("{")) {
        try {
          feedKindProduct =
            (JSON.parse(detailsRaw) as { feedKind?: string })?.feedKind ===
            "product-feed";
        } catch {
          feedKindProduct = false;
        }
      }
      // Latest Product Feed — same featured RPC as homepage strip / FeaturedData.
      if (filterLc === "products" || feedKindProduct) {
        return fetchFeaturedFirst(row, userId, lcUser);
      }
      return fetchTypeContentFirst(row, userId, lcUser, "instagram", "REELS");
    }
    // Featured / videos with no static post_ids still have a live feed (FeaturedData).
    if (type === "featured" || match("featured")) {
      return fetchFeaturedFirst(row, userId, lcUser);
    }
  }

  if (
    dnLc.includes("instagram") ||
    dnLc.includes("reel") ||
    dnLc.includes("ig ")
  ) {
    const reels = await fetchTypeContentFirst(
      row,
      userId,
      lcUser,
      "instagram",
      "REELS",
    );
    if (reels) return reels;
    return fetchTypeContentFirst(row, userId, lcUser, "instagram", "POST");
  }

  if (dnLc.includes("tiktok")) {
    return fetchTypeContentFirst(row, userId, lcUser, "tiktok", "TIKTOK");
  }

  if (dnLc.includes("youtube")) {
    return fetchTypeContentFirst(row, userId, lcUser, "youtube", "VIDEO");
  }

  if (match("featured")) {
    return fetchFeaturedFirst(row, userId, lcUser);
  }

  if (type === "videos") {
    return fetchCustomOrderedFirst(row, userId, lcUser, type);
  }

  // Product / shoppable carousels often live under these types with post_ids.
  if (
    type === "product-bank" ||
    type === "store-product" ||
    type === "products-carousel" ||
    type === "trending products" ||
    type === "trending content"
  ) {
    const rawPid = (row as { post_ids?: unknown }).post_ids;
    const postIdsArr = Array.isArray(rawPid)
      ? rawPid.map(String).filter(Boolean)
      : typeof rawPid === "string"
        ? rawPid.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
    if (postIdsArr.length > 0) {
      return fetchCustomOrderedFirst(row, userId, lcUser, "links");
    }
    if (type === "trending products" || type === "trending content") {
      return fetchFeaturedFirst(row, userId, lcUser);
    }
  }

  return null;
}
